import React, { useState } from 'react'
import {
  Button,
  ModalInjectedProps,
  Tabs,
  Tab,
  TabPanel,
  LinkButton,
  Modal as BasicModal,
} from '@sketch/components'
import { routes } from '@sketch/modules-common'
import { WorkspaceMinimalFragment, ProjectFragment } from '@sketch/gql-types'
import { ProjectSharingHeader } from './ProjectSharingHeader'
import { WorkspacePanel } from './Workspace/WorkspacePanel'
import { GuestsPanel } from './Guests/GuestsPanel'
import { LeaveProjectButton } from './LeaveProjectButton'
import {
  StyledModal as Modal,
  StyledTabList as TabList,
  TabPanelWrapper,
} from '../../../shares/modals/SharingModal/SharingModal.styles'
import { ModalHeader } from './ProjectSharingModal.styles'
import { GuestsTitle } from '../../../shares/modals/SharingModal/GuestsPanel'
import useProjectMemberships from './useProjectMemberships'
import { useGetRootProject } from '../../operations'
import { ProjectArchivedDropdownProps } from '../../components/ProjectDropdown/ProjectArchivedDropdown'

interface ProjectSharingModalProps extends ModalInjectedProps {
  project: ProjectArchivedDropdownProps['project']
  workspace: WorkspaceMinimalFragment
}
type WorkspaceAccessLevel = ProjectFragment['workspaceAccessLevel']

export const ProjectSharingModal: React.FC<ProjectSharingModalProps> = ({
  project,
  workspace,
  hideModal,
}) => {
  const [workspaceAccessLevel, setWorkspaceAccessLevel] =
    useState<WorkspaceAccessLevel>(project.workspaceAccessLevel)

  const rootProject = useGetRootProject(
    workspace.identifier,
    project.identifier
  )

  let rootArchivedProject

  // If there's no root project, that means this is an archived folder
  if (!rootProject) {
    rootArchivedProject = {
      project: {
        identifier: project.topLevelProject?.identifier ?? project.identifier,
        name: project.topLevelProject?.name ?? project.name,
      },
      link: routes.WORKSPACE_PROJECT.create({
        projectId: project.topLevelProject?.identifier ?? project.identifier,
        workspaceId: workspace.identifier,
      }),
      nestedProjects: [],
    }
  }

  const parsedRootProject = rootProject || rootArchivedProject

  const hasAccess = workspaceAccessLevel === 'DEFAULT'

  const { guestsCount } = useProjectMemberships(
    project.topLevelProject?.identifier || project.identifier
  )

  // We only a show the share modal for root folder, nested folders inherit
  // the share settings from the root folder
  return project.parentProjectIdentifier ? (
    <BasicModal onCancel={hideModal}>
      <ModalHeader>
        <strong>Share:</strong> {project.name}
      </ModalHeader>
      <BasicModal.Body>
        This folder uses the same Share Settings as “
        {parsedRootProject.project.name}
        ”.
      </BasicModal.Body>
      <BasicModal.Footer
        leftContent={
          // TODO: add correct link
          // https://github.com/sketch-hq/Cloud/issues/19635
          <LinkButton
            external
            href="https://www.sketch.com/support/"
            onClick={() => hideModal()}
          >
            Learn More ↗
          </LinkButton>
        }
      >
        <LinkButton to={parsedRootProject.link}>View Root Folder</LinkButton>
        <Button variant="primary" onClick={hideModal}>
          Done
        </Button>
      </BasicModal.Footer>
    </BasicModal>
  ) : (
    <Modal onCancel={hideModal}>
      <ProjectSharingHeader
        project={project}
        workspaceId={workspace.identifier}
        workspaceName={workspace.name}
      />
      <Modal.Body>
        <Tabs>
          <TabList shouldDrawSeparator>
            <Tab>Workspace Members {!hasAccess && `(Restricted)`}</Tab>
            <Tab>
              <GuestsTitle guestsCount={guestsCount} />
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelWrapper>
              <WorkspacePanel
                workspace={workspace}
                project={project}
                setWorkspaceAccessLevel={setWorkspaceAccessLevel}
                hasAccess={hasAccess}
              />
            </TabPanelWrapper>
          </TabPanel>
          <TabPanel>
            <TabPanelWrapper>
              <GuestsPanel workspace={workspace} project={project} />
            </TabPanelWrapper>
          </TabPanel>
        </Tabs>
      </Modal.Body>
      <Modal.Footer
        leftContent={
          project.userCanLeave ? (
            <LeaveProjectButton
              workspaceIdentifier={workspace.identifier}
              project={project}
            />
          ) : undefined
        }
      >
        <Button variant="primary" onClick={hideModal}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
